import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../Utils/axiosInstance";
import { toast } from "react-hot-toast";

// Fetch active discounts/banners for the public side
export const fetchActiveDiscounts = createAsyncThunk(
  "discount/fetchActive",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/discounts");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Fetch all discounts for admin dashboard
export const fetchDiscountsAdmin = createAsyncThunk(
  "discount/fetchAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/discounts/admin");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Create a discount (multipart/form-data)
export const createDiscount = createAsyncThunk(
  "discount/create",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/discounts", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message || "Discount created successfully");
      return res.data.data;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to create discount");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Update a discount (multipart/form-data)
export const updateDiscount = createAsyncThunk(
  "discount/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/discounts/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message || "Discount updated successfully");
      return res.data.data;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to update discount");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Delete a discount
export const deleteDiscount = createAsyncThunk(
  "discount/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/discounts/${id}`);
      toast.success(res.data.message || "Discount deleted successfully");
      return id;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to delete discount");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Validate promo code
export const validatePromoCode = createAsyncThunk(
  "discount/validatePromo",
  async (code, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/discounts/validate-promo", { code });
      toast.success(res.data.message || "Promo code applied");
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Invalid promo code");
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const discountSlice = createSlice({
  name: "discount",
  initialState: {
    activeDiscounts: [],
    allDiscounts: [],
    promoCodeInfo: null,
    loading: false,
    error: null,
    promoLoading: false,
    promoError: null,
  },
  reducers: {
    clearPromoCode: (state) => {
      state.promoCodeInfo = null;
      state.promoError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Active discounts
      .addCase(fetchActiveDiscounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveDiscounts.fulfilled, (state, action) => {
        state.loading = false;
        state.activeDiscounts = action.payload.data || [];
      })
      .addCase(fetchActiveDiscounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch active discounts";
      })

      // Admin fetch
      .addCase(fetchDiscountsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDiscountsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.allDiscounts = action.payload.data || [];
      })
      .addCase(fetchDiscountsAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch discounts";
      })

      // Create
      .addCase(createDiscount.fulfilled, (state, action) => {
        state.allDiscounts.unshift(action.payload);
      })

      // Update
      .addCase(updateDiscount.fulfilled, (state, action) => {
        state.allDiscounts = state.allDiscounts.map((d) =>
          d._id === action.payload._id ? action.payload : d
        );
      })

      // Delete
      .addCase(deleteDiscount.fulfilled, (state, action) => {
        state.allDiscounts = state.allDiscounts.filter((d) => d._id !== action.payload);
      })

      // Validate Promo
      .addCase(validatePromoCode.pending, (state) => {
        state.promoLoading = true;
        state.promoError = null;
        state.promoCodeInfo = null;
      })
      .addCase(validatePromoCode.fulfilled, (state, action) => {
        state.promoLoading = false;
        state.promoCodeInfo = action.payload;
      })
      .addCase(validatePromoCode.rejected, (state, action) => {
        state.promoLoading = false;
        state.promoError = action.payload || "Invalid promo code";
      });
  },
});

export const { clearPromoCode } = discountSlice.actions;
export default discountSlice.reducer;
